import React, { useCallback } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { yupResolver } from '@hookform/resolvers/yup'
import toast from 'react-hot-toast'
import ReleaseNoteLayout, { ReleaseNoteMain, ReleaseNoteSidebar } from './ReleaseNoteLayout'
import SwitchInput from 'Design/components/SwitchInput'

import ReleaseNoteForm from './ReleaseNoteForm'
import releaseNoteFormValidationSchema from './ReleaseNoteForm/release-note-form-validation-schema'
import { useCreateReleaseNoteMutation } from './release-notes.service'
import parseErrorMessage from 'Utils/error-message-parser'

import Tags from '../../../../components/Tags'
import { CATEGORIES } from './constants'
let categoriesOptions

const CategoriesInput = ({ field: { value, onChange } }) => {
  const handleTagRemove = (tag) => {
    const newTags = value.filter((t) => t.value !== tag.value)
    onChange(newTags)
  }

  return (
    <Tags
      placeholder='Add categories'
      value={value}
      onChange={onChange}
      onRemove={handleTagRemove}
      options={generateCategoryOptions()}
    />
  )
}

const CreateReleaseNote = () => {
  const navigate = useNavigate()

  const formMethods = useForm({
    defaultValues: {
      title: '',
      body: '',
      is_visible: true,
      product: {
        label: 'Select a product',
        value: null
      },
      categories: [],
      release_date: new Date().toISOString()
    },
    resolver: yupResolver(releaseNoteFormValidationSchema)
  })

  const [createReleaseNote, releaseNoteResult] = useCreateReleaseNoteMutation()

  const isLoading = releaseNoteResult.isLoading

  const onSubmit = useCallback(
    async (data, navigateToEdit) => {
      const payload = {
        title: data.title,
        body: data.body,
        is_visible: data.is_visible,
        categories: data.categories.map((category) => category.value),
        product_id: data.product?.value,
        release_date: data.release_date
      }

      try {
        const promise = createReleaseNote(payload).unwrap()

        toast.promise(promise, {
          loading: 'Creating release note',
          success: 'Release note created successfully',
          error: parseErrorMessage('Unable to create release note. Try again...')
        })

        if (navigateToEdit) {
          const releaseNote = await promise
          navigate('../' + releaseNote.id, { replace: true })
        }
      } catch (error) {
        console.error('Failed to create release note. Error:', error)
      }
    },
    [createReleaseNote, navigate]
  )

  const handleSaveAndAddNewReleaseNoteClick = useCallback(
    (data) => {
      const goToEditReleaseNote = false
      onSubmit(data, goToEditReleaseNote)
      formMethods.reset()
    },
    [formMethods, onSubmit]
  )

  return (
    <ReleaseNoteLayout>
      <ReleaseNoteMain>
        <FormProvider {...formMethods}>
          <ReleaseNoteForm
            mode='create'
            onSubmit={onSubmit}
            isLoading={isLoading}
            onSaveAndAddNewReleaseNote={handleSaveAndAddNewReleaseNoteClick}
          />
        </FormProvider>
      </ReleaseNoteMain>
      <ReleaseNoteSidebar>
        <Controller
          control={formMethods.control}
          name='is_visible'
          render={({ field: { value, onChange } }) => {
            return (
              <SwitchInput checked={value} onChange={onChange}>
                Visible
              </SwitchInput>
            )
          }}
        />
        <Controller control={formMethods.control} name='categories' render={CategoriesInput} />
      </ReleaseNoteSidebar>
    </ReleaseNoteLayout>
  )
}

const generateCategoryOptions = () => {
  if (!categoriesOptions) {
    categoriesOptions = []
    for (const key in CATEGORIES) {
      const label = CATEGORIES[key]
      const value = key
      categoriesOptions.push({
        label,
        value
      })
    }
  }
  return categoriesOptions
}

export default CreateReleaseNote
